import type { ApiError } from '@/lib/api';

export type ErrorKind =
  | 'network'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'validation'
  | 'server'
  | 'unknown';

const NETWORK_MESSAGE = /failed to fetch|load failed|networkerror|network request failed/i;
// A single token such as internal_error or Unauthorized is a machine code, not prose.
const MACHINE_CODE = /^[A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)*$/;

export function classifyError(err: unknown): ErrorKind {
  if (err instanceof TypeError) {
    return NETWORK_MESSAGE.test(err.message) ? 'network' : 'unknown';
  }
  const status = (err as ApiError | null | undefined)?.status;
  if (typeof status !== 'number') return 'unknown';
  if (status === 401 || status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

/**
 * Human-written server text worth showing; null for machine codes, fallbacks and noise.
 * Only an error carrying an HTTP status (ApiError, or a raw fetch with `status` attached) may
 * surface its message: a client-constructed Error is English source text, never shown as prose.
 */
export function serverText(err: unknown): string | null {
  if (typeof (err as ApiError | null | undefined)?.status !== 'number') return null;
  const msg = (err as Error | null | undefined)?.message?.trim();
  if (!msg || msg.length > 200) return null;
  if (MACHINE_CODE.test(msg)) return null;
  if (/^Request failed with \d+$/.test(msg)) return null;
  if (/^HTTP \d+$/.test(msg)) return null;
  // A raw HTML or proxy error body, not prose.
  if (msg.startsWith('<')) return null;
  if (NETWORK_MESSAGE.test(msg)) return null;
  return msg;
}
